// BattleScene mixin extraction #3 (structural-only) — the battle-DRIVING
// surface.
//
// run loop + choice gate, action/force-switch menus + bag flow,
// runFromBattle, the runState flush/splice trio, and the small host
// utilities (appendLog, wait, refreshPlayerExp,
// updateLastAliveCriticalState, applyHpBarType) extracted verbatim from
// BattleScene.ts so a future WorldBattleHost can adopt the full
// battle-driving contract without forking bookkeeping. Zero behavior
// change: each function is invoked via `.call(this, ...)` from a
// one-line delegator on BattleScene, preserving the original
// `this`-binding semantics.
//
// `BattleScene` is imported type-only to avoid a runtime circular
// dependency (BattleScene.ts imports this module as a value to wire the
// delegators).
import type { TurnRequest } from '../battle/types';
import {
  startInteractiveBattle,
  type InteractiveBattle,
} from '../battle/runInteractiveBattle';
import {
  TRAINER_FLEE_ALLOWED,
  TRAINER_FLEE_REFUSAL_LINE,
  fleeOutcome,
  fledTrainerFlagResets,
} from '../battle/battleRules';
import { runState } from '../state/runState';
import { saveManager } from '../state/saveManager';
import { audio } from '../systems/audio';
import { loadGen3 } from '../data/dex';
import { effectivenessAgainst } from '../battle/typeChart';
import { usableBattleItems, checkBattleUse } from '../battle/battleBag';
import { NO_EFFECT } from '../state/bagUse';
import { applyStatusTint } from './BattleScene.eventHandlers';
import type {
  ActionResult,
  BagTargetView,
  BattleScene,
} from './BattleScene';

/**
 * Block until the event-playback loop signals it's reached a "ready for
 * next move" point (the next 'turn' event, or battleEnd). Each call
 * consumes one signal — successive choices each have to wait for their
 * own turn-event gate.
 */
export function waitForChoicesReady(this: BattleScene): Promise<void> {
  if (this.choicesReady) {
    this.choicesReady = false;
    return Promise.resolve();
  }
  return new Promise<void>((resolve) => {
    this.choicesReadyResolver = resolve;
  }).then(() => {
    this.choicesReady = false;
  });
}

export function signalChoicesReady(this: BattleScene): void {
  if (this.choicesReadyResolver) {
    const r = this.choicesReadyResolver;
    this.choicesReadyResolver = undefined;
    r();
  } else {
    this.choicesReady = true;
  }
}

/** Back-out one step: bagTarget → bag → main; moves/switch → main.
 *  No-op during force-switch (mandatory) or main mode. Bag modes are
 *  unreachable during force-switch (the flow jumps straight to the
 *  switch menu), so the force-switch guard stays on moves/switch. */
export function onBackClick(this: BattleScene): void {
  if (this.menuMode === 'bagTarget') {
    audio.playSfx('select');
    this.pendingBagItemId = null;
    this.showBagMenu();
    return;
  }
  if (this.menuMode === 'bag') {
    audio.playSfx('select');
    this.showMainMenu();
    return;
  }
  if (this.menuMode !== 'moves' && this.menuMode !== 'switch') return;
  if (this.pendingForceSwitchResolver) return;
  audio.playSfx('select');
  this.showMainMenu();
}

export function hideActionButtons(this: BattleScene): void {
  this.battleActionsHandle?.hide();
}

export function onActionButtonClick(this: BattleScene, slot: number): void {
  if (this.menuMode === 'main') {
    // FIGHT / POKEMON / BAG / RUN
    switch (slot) {
      case 1:
        this.showMoveMenu();
        break;
      case 2:
        this.showSwitchMenu();
        break;
      case 3:
        this.showBagMenu();
        break;
      case 4:
        void this.runFromBattle();
        break;
    }
    return;
  }
  if (this.menuMode === 'bag') {
    const row = this.bagRows[slot - 1];
    if (!row) return;
    audio.playSfx('select');
    this.pendingBagItemId = row.itemId;
    this.showBagTargetMenu();
    return;
  }
  if (this.menuMode === 'bagTarget') {
    this.confirmBagUse(slot);
    return;
  }
  if (this.menuMode === 'moves' && this.pendingActionResolver) {
    // Validate against this turn's available moves — empty / disabled slots
    // shouldn't submit even if their button somehow gets clicked.
    const move = this.pendingRequest?.moves?.[slot - 1];
    if (!move || move.disabled) return;
    const resolve = this.pendingActionResolver;
    this.pendingActionResolver = undefined;
    this.menuMode = 'none';
    this.hideActionButtons();
    resolve({ kind: 'move', slot });
    return;
  }
  if (this.menuMode === 'switch') {
    const team = this.pendingRequest?.team ?? [];
    // Filter to non-active, non-fainted slots — these are the buttons we
    // actually rendered. The button index `slot - 1` indexes into THAT
    // filtered list, not the raw team[].
    const switchable = team.filter((s) => !s.active && !s.fainted);
    const target = switchable[slot - 1];
    if (!target) return;
    // Force-switch path: separate resolver (the player has no other choice).
    if (this.pendingForceSwitchResolver) {
      const r = this.pendingForceSwitchResolver;
      this.pendingForceSwitchResolver = undefined;
      this.menuMode = 'none';
      this.hideActionButtons();
      r(target.slot);
      return;
    }
    // Normal turn path: switch counts as that turn's action.
    if (this.pendingActionResolver) {
      const r = this.pendingActionResolver;
      this.pendingActionResolver = undefined;
      this.menuMode = 'none';
      this.hideActionButtons();
      r({ kind: 'switch', slot: target.slot });
    }
  }
}

/**
 * Show the top-level FIGHT / POKEMON / BAG / RUN menu. Called when a new
 * turn's choice gate releases. The pendingRequest from the simulator
 * stays cached so subsequent FIGHT/POKEMON clicks can populate their
 * sub-menus without re-querying.
 */
export function showMainMenu(this: BattleScene): void {
  this.menuMode = 'main';
  // ASCII labels — m5x7's atlas doesn't include the É glyph so "POKÉMON"
  // renders as "POKMON" — m5x7 retired but the plain-caps cartridge
  // feel is preserved as a stylistic choice.
  const labels = ['FIGHT', 'POKEMON', 'BAG', 'RUN'];
  this.battleActionsHandle?.setButtons(
    labels.map((text) => ({ text })),
  );
  this.battleActionsHandle?.setBackVisible(false);
}

export function showMoveMenu(this: BattleScene): void {
  if (!this.pendingRequest) return;
  this.menuMode = 'moves';
  const moves = this.pendingRequest.moves;
  // Show buttons immediately with type=undefined (text-primary border
  // on focus); type-color borders patch in once @pkmn/dex resolves.
  // This avoids blocking the menu render on the dex chunk fetch the
  // first time the player picks FIGHT in a session.
  const labels = Array.from({ length: 4 }, (_, i) => {
    const move = moves[i];
    if (!move) return null;
    const text = move.disabled
      ? `${move.name}\n(disabled)`
      : `${move.name}\n${move.pp}/${move.maxpp} PP`;
    return { text, disabled: move.disabled } as const;
  });
  this.battleActionsHandle?.setButtons(labels);
  this.battleActionsHandle?.setBackVisible(true);
  // Patch in move types + matchup badges asynchronously — fire-and-
  // forget. Each move's type drives the focus border color (e.g. Tackle
  // = normal-gray, Ember = fire-orange). PR 2.5: matchup badge resolves
  // via typeChart against the current opponent's types. typeChart is
  // preloaded at battle mount so the lookup is sync; loadGen3 here
  // still resolves the move's TYPE (effectivenessAgainst takes the
  // type string, not a move id).
  void (async () => {
    try {
      const gen = await loadGen3();
      // Resolve current opponent's primary + secondary types. v0.1
      // wild battles + trainer battles both send opponents in team
      // order, so opponentFaints.length is the live one.
      const oppIdx = this.opponentFaints.length;
      const oppSpecies = this.params.setup.p2.team[oppIdx]?.species;
      const oppTypes: string[] = oppSpecies
        ? (gen.species.get(oppSpecies)?.types ?? []).map((t) => t.toLowerCase())
        : [];
      const typed = Array.from({ length: 4 }, (_, i) => {
        const move = moves[i];
        if (!move) return null;
        const moveData = gen.moves.get(move.id);
        const type = moveData?.type?.toLowerCase();
        const text = move.disabled
          ? `${move.name}\n(disabled)`
          : `${move.name}\n${move.pp}/${move.maxpp} PP`;
        let matchup: 'good' | 'neutral' | 'bad' | 'none' | undefined;
        if (type && oppTypes.length > 0) {
          const eff = effectivenessAgainst(type, oppTypes);
          matchup =
            eff === 0 ? 'none'
            : eff > 1 ? 'good'
            : eff < 1 ? 'bad'
            : 'neutral';
        }
        return type
          ? { text, type, disabled: move.disabled, matchup }
          : { text, disabled: move.disabled, matchup };
      });
      if (this.menuMode === 'moves') {
        this.battleActionsHandle?.setButtons(typed);
      }
    } catch {
      // Dex load failed — leave the buttons at their default border.
    }
  })();
}

/**
 * G10: the BAG sub-menu — heal/cure stacks on the same 4-button
 * surface the switch menu uses. Empty bag keeps the main menu open
 * with a log beat (sibling of showSwitchMenu's empty branch).
 */
export function showBagMenu(this: BattleScene): void {
  const rows = usableBattleItems(runState.getBag());
  if (rows.length === 0) {
    this.appendLog('You have no usable items!');
    return;
  }
  this.menuMode = 'bag';
  this.bagRows = rows.slice(0, 4);
  if (rows.length > 4) {
    // The action surface is a fixed 2×2. Registry order is stable,
    // so the same four stacks show every turn; overflowing needs 5+
    // distinct heal/cure types (scope max is 7) — a log note beats
    // a pager for that corner.
    this.appendLog('(Your BAG holds more than fits here.)');
  }
  this.battleActionsHandle?.setButtons(
    Array.from({ length: 4 }, (_, i) => {
      const row = this.bagRows[i];
      return row ? { text: `${row.name} x${row.count}\n${row.desc}` } : null;
    }),
  );
  this.battleActionsHandle?.setBackVisible(true);
}

/**
 * G10: the bag target picker — living party members (active included,
 * marked ●) with battle-truth HP + status. HP comes from hpById (the
 * owned model — request data lies on turn 1); status from the sim
 * snapshot (a mon paralyzed THIS battle must read as paralyzed here),
 * falling back to runState on snapshot failure.
 */
export function showBagTargetMenu(this: BattleScene): void {
  const statuses = this.battle?.snapshotP1Statuses() ?? [];
  this.bagTargets = runState
    .getParty()
    .map((m, i): BagTargetView => {
      const hp = this.hpById.get(m.id) ?? { current: m.hp.current, max: m.hp.max };
      return {
        id: m.id,
        simSlot: i + 1,
        displayName: m.species.charAt(0).toUpperCase() + m.species.slice(1),
        level: m.level,
        hpCurrent: hp.current,
        hpMax: hp.max,
        status: statuses.length > i ? statuses[i]! : m.status,
        fainted: this.faintedIds.has(m.id) || hp.current <= 0,
        active: m.id === this.activeId,
      };
    })
    .filter((t) => !t.fainted)
    .slice(0, 4); // 2×2 surface; ≥5 living members is a 6-party edge
  this.menuMode = 'bagTarget';
  this.battleActionsHandle?.setButtons(
    Array.from({ length: 4 }, (_, i) => {
      const t = this.bagTargets[i];
      if (!t) return null;
      const status = t.status ? `  ${t.status.toUpperCase()}` : '';
      return {
        text: `${t.displayName}${t.active ? ' ●' : ''}\nLv ${t.level}  ${t.hpCurrent}/${t.hpMax}${status}`,
      };
    }),
  );
  this.battleActionsHandle?.setBackVisible(true);
}

/**
 * G10 confirm: validate → sim item turn → consume → display writes.
 * Ordering is the trust contract: the item is only removed AFTER
 * useItemTurn returns true (its false consumes neither turn nor
 * item), and a no-effect check keeps the player in the picker to
 * choose someone else (cartridge behavior).
 */
export function confirmBagUse(this: BattleScene, slot: number): void {
  const target = this.bagTargets[slot - 1];
  const itemId = this.pendingBagItemId;
  if (!target || !itemId || !this.battle || !this.pendingActionResolver) return;

  const check = checkBattleUse(itemId, target);
  if (!check.ok) {
    audio.playSfx('select');
    this.appendLog(check.message);
    return;
  }
  const applied = this.battle.useItemTurn({
    targetIndex: target.simSlot - 1,
    effect: check.effect,
  });
  if (!applied) {
    // Sim-side no-op — model drift between our check and sim truth.
    // Nothing was consumed; stay in the picker.
    this.appendLog(NO_EFFECT);
    return;
  }
  // The sim committed the turn; consume the stack. False here means
  // the bag drifted under a live picker (no mid-battle consumer
  // exists) — warn, never unwind a committed turn.
  if (!runState.removeItem(itemId)) {
    console.warn('[battle] bag count drifted under an item turn:', itemId);
  }
  // Display-model writes. useItemTurn mutates sim state directly, so
  // NO protocol event will arrive for the heal/cure — the event
  // stream can't do this for us.
  if (check.healedTo !== undefined) {
    this.hpById.set(target.id, { current: check.healedTo, max: target.hpMax });
    if (target.active) {
      this.playerHpPercent = (check.healedTo / target.hpMax) * 100;
      this.playerStatusPanel?.setHp(this.playerHpPercent / 100);
      this.playerStatusPanel?.setHpNumeric(check.healedTo, target.hpMax);
      // A heal can un-latch the last-alive critical pulse.
      this.updateLastAliveCriticalState();
    }
  } else if (target.active) {
    // Cure on the active battler — clear tint/flicker now; reserves
    // re-tint correctly on switch-in from sim status.
    applyStatusTint(this, this.playerSprite, null);
  }
  for (const line of check.lines) this.appendLog(line);
  audio.playSfx('heal');

  const resolve = this.pendingActionResolver;
  this.pendingActionResolver = undefined;
  this.pendingBagItemId = null;
  this.menuMode = 'none';
  this.hideActionButtons();
  resolve({ kind: 'item' });
}

/**
 * Show the POKEMON switch sub-menu. Lists party members that are NOT
 * the active battler and NOT fainted — those are the legal switch
 * targets. Tapping one fires `>p1 switch <slot>` via the choice loop
 * and counts as this turn's action.
 *
 * If there are no switchable members (only one survivor), we surface
 * a log message and keep the main menu open.
 */
export function showSwitchMenu(this: BattleScene): void {
  if (!this.pendingRequest) return;
  const team = this.pendingRequest.team;
  const switchable = team.filter((s) => !s.active && !s.fainted);
  if (switchable.length === 0) {
    this.appendLog('No other POKEMON ready!');
    return;
  }
  this.menuMode = 'switch';
  const labels = Array.from({ length: 4 }, (_, i) => {
    const member = switchable[i];
    if (!member) return null;
    const hpFrac = member.hpMax > 0 ? Math.round((member.hpCurrent / member.hpMax) * 100) : 0;
    return { text: `${member.displayName}\nLv ${member.level}  ${hpFrac}%` };
  });
  this.battleActionsHandle?.setButtons(labels);
  // Back is allowed on a normal-turn POKEMON pick (player can change
  // their mind), but NOT on a forced switch after a faint — there's
  // no main menu to back into in that flow.
  this.battleActionsHandle?.setBackVisible(!this.pendingForceSwitchResolver);
  // Patch in each switchable member's primary type asynchronously for
  // type-color focus borders.
  void (async () => {
    try {
      const gen = await loadGen3();
      const typed = Array.from({ length: 4 }, (_, i) => {
        const member = switchable[i];
        if (!member) return null;
        const speciesData = gen.species.get(member.species);
        const type = speciesData?.types[0]?.toLowerCase();
        const hpFrac = member.hpMax > 0 ? Math.round((member.hpCurrent / member.hpMax) * 100) : 0;
        const text = `${member.displayName}\nLv ${member.level}  ${hpFrac}%`;
        return type ? { text, type } : { text };
      });
      if (this.menuMode === 'switch') {
        this.battleActionsHandle?.setButtons(typed);
      }
    } catch {
      // Dex load failed — leave the buttons at their default border.
    }
  })();
}

export function promptAction(this: BattleScene): Promise<ActionResult> {
  return new Promise<ActionResult>((resolve) => {
    this.pendingActionResolver = resolve;
    this.showMainMenu();
  });
}

/**
 * Force-switch prompt: fired after the active fainted with reserves
 * remaining. Player can ONLY pick a switch target; no FIGHT/BAG/RUN.
 * Skips the main menu and opens the switch sub-menu directly.
 */
export function promptForceSwitch(this: BattleScene): Promise<number> {
  return new Promise<number>((resolve) => {
    this.pendingForceSwitchResolver = resolve;
    this.showSwitchMenu();
  });
}

/**
 * Player picked RUN. Cartridge-accurate v0.1 behavior is "always succeeds"
 * — no Pokémon-Showdown command exists for fleeing a wild battle (the sim
 * is built for trainer matches), so we exit at the BattleScene level. The
 * underlying BattleStream is left dangling and GC'd when the scene shuts
 * down; the small per-battle leak is acceptable for v0.1.
 */
export async function runFromBattle(this: BattleScene): Promise<void> {
  // Round-4 policy (concession design rescinded pending litigation):
  // trainer flee is a TESTING-PHASE affordance behind
  // TRAINER_FLEE_ALLOWED. When disallowed: the cartridge refusal
  // beat — log line, main menu stays live, no resolver fires, the
  // player still owes the turn. When allowed: flee leaves the
  // encounter OPEN and the world coherent — the rival's pre-battle
  // beats hid his NPC + set his self-gate flag future-tense, and
  // fleeing used to save that as a soft-lock (empty lab,
  // unre-triggerable — the round-4 bug). fledTrainerFlagResets
  // un-hides + re-arms him; the post-flee WAKE puts him back at the
  // table, row-8 re-triggers, tap taunts via the POST_PICK dispatch.
  // Generic trainers need nothing. Wild flee unchanged.
  const fleeRef = this.params.trainerScriptRef;
  if (fleeOutcome(fleeRef, TRAINER_FLEE_ALLOWED) === 'refuse') {
    this.appendLog(TRAINER_FLEE_REFUSAL_LINE);
    return;
  }
  if (fleeRef) {
    for (const flag of fledTrainerFlagResets(fleeRef)) {
      runState.clearFlag(flag);
    }
  }
  this.menuMode = 'none';
  this.hideActionButtons();
  this.appendLog('Got away safely!');
  this.battleOver = true;
  // Unstick anything in the choice loop that's still waiting on signals.
  this.signalChoicesReady();
  if (this.pendingActionResolver) {
    const r = this.pendingActionResolver;
    this.pendingActionResolver = undefined;
    r({ kind: 'run' });
  }
  if (this.pendingForceSwitchResolver) {
    // Force-switch can't really be cancelled (sim won't proceed otherwise),
    // but we're exiting the scene entirely so freeing the resolver is fine.
    this.pendingForceSwitchResolver = undefined;
  }
  // Flush + splice BEFORE the save so the snapshot reflects damaged HP +
  // the permadeath splice. Without this the next encounter re-reads
  // runState's stale pre-battle HP (= full health) and the orb strip never
  // hears about the dead member.
  this.flushSurvivorHpToRunState();
  if (this.battle) this.flushBattleStatusToRunState(this.battle);
  this.spliceFaintedFromRunState();
  saveManager.save();
  await this.wait(1500);
  this.exitToWorld();
}

/**
 * Copy every survivor's mid-battle hpById entry back into runState. Called
 * on EVERY battle exit path (battleEnd + runFromBattle) — the contract is
 * "the runState mirror must reflect the post-battle truth before
 * saveManager.save flushes it." Skips faintedIds (the splice removes them
 * next anyway; writing 0 HP and then splicing is wasted work).
 *
 * Must run BEFORE EXP awards on the battleEnd path — addExp's level-up
 * full-heal should be the final word on a survivor's HP. Reversing the
 * order would let the mid-battle stored HP overwrite the leveled-up max.
 */
export function flushSurvivorHpToRunState(this: BattleScene): void {
  for (const m of runState.getParty()) {
    if (this.faintedIds.has(m.id)) continue;
    const stored = this.hpById.get(m.id);
    if (stored) runState.setMemberHpById(m.id, stored.current);
  }
}

/**
 * PR 3 EXP 5b: flush every non-fainted party member's persistent
 * status condition back to runState, reading from sim's internal
 * pokemon[i].status via the InteractiveBattle handle. Called from
 * battleEnd + runFromBattle alongside the HP flush.
 *
 * Iterates ALL party slots (active + reserves), not just the active
 * one — the active member's status may have just changed mid-turn,
 * but reserves can also carry status across switches.
 *
 * Slot alignment: sim's p1Side.pokemon[] is in party order at battle
 * start; the |switch|p1a stream events shuffle which is ACTIVE but
 * the array order doesn't reshuffle. So party[i].status ←→ sim
 * slot i. Fainted slots are about to be spliced — skip the write.
 *
 * Defensive: the snapshot helper returns [] on shape mismatch. In
 * that case we do nothing (keep prior runState status values).
 */
export function flushBattleStatusToRunState(
  this: BattleScene,
  battle: InteractiveBattle,
): void {
  const statuses = battle.snapshotP1Statuses();
  if (statuses.length === 0) return; // shape mismatch — prior values stay
  const party = runState.getParty();
  for (let i = 0; i < party.length; i++) {
    const m = party[i]!;
    if (this.faintedIds.has(m.id)) continue;
    const status = statuses[i] ?? null;
    runState.setMemberStatusById(m.id, status);
  }
}

/**
 * Permadeath splice + per-loss log line. Called on every battle exit path.
 * No-op when faintedIds is empty (most battles). The "X has been
 * lost." dialogue already fires per-faint via runPlayerFaintCinematic
 * — no log line needed here. Splice fires onMembersRemoved, which
 * the orb strip subscribes to for the tombstone cascade.
 */
export function spliceFaintedFromRunState(this: BattleScene): void {
  if (this.faintedIds.size === 0) return;
  runState.removeWhere((m) => this.faintedIds.has(m.id));
}

export async function run(this: BattleScene): Promise<void> {
  // Cartridge opener: wild battles announce the species; trainer
  // battles keep the generic line (trainer intro copy lives in the
  // pre-battle dialogue, not the log).
  if (!this.params.trainerScriptRef) {
    const wild = this.params.setup.p2.team[0];
    const wildName = wild?.name ?? wild?.species ?? '';
    this.appendLog(
      wildName ? `Wild ${wildName.toUpperCase()} appeared!` : 'Battle start!',
    );
  } else {
    this.appendLog('Battle start!');
  }
  let battle: InteractiveBattle;
  try {
    battle = await startInteractiveBattle(this.params.setup);
  } catch (err) {
    console.error('battle failed to start:', err);
    this.appendLog('Battle errored — returning to overworld.');
    await this.wait(1500);
    this.exitToWorld();
    return;
  }
  this.battle = battle;

  // Choice loop: keep pulling p1 choice prompts; for each, show move
  // buttons and wait for the player to click one. Runs concurrently with
  // the event loop below — but each prompt is gated on
  // waitForChoicesReady(), which the event loop signals at every 'turn'
  // event. That keeps the UI's choice cadence in lockstep with the
  // animation cadence (rather than letting the simulator queue prompts
  // multiple turns ahead and letting the player pre-commit moves).
  void (async () => {
    let req: TurnRequest | null;
    while ((req = await battle.nextP1Choice()) !== null) {
      this.pendingRequest = req;
      // Track the active sim slot from the request as a baseline. NOTE:
      // requests only arrive at turn boundaries, so mid-turn switches
      // (player switches in Spe + opp KOs Spe same turn) update
      // activeSlot via the |switch| event handler in playEvent — that
      // path is what guarantees |faint| credits the correct party slot
      // to permadeath.
      const active = req.team.find((s) => s.active);
      if (active) this.activeSlot = active.slot;

      if (req.forceSwitch) {
        // Force-switch fires immediately after a faint event. The
        // choice gate (waitForChoicesReady) waits for the *next* turn
        // marker — which sim won't emit until AFTER the player switches.
        // Waiting here would deadlock the battle ("frozen on fainted
        // page"). The faint tween has already played by the time we
        // get here, so it's safe to prompt directly.
        if (this.battleOver) return;
        this.appendLog('Choose your next POKEMON.');
        const slot = await this.promptForceSwitch();
        if (this.battleOver) return;
        // v7: stamp the chosen party member's id so the matching
        // |switch|p1a event resolves to the correct instance even when
        // two same-species duplicates coexist. runState.party order
        // mirrors setup.p1.team order; slot is 1-indexed.
        this.pendingSwitchToId = runState.getParty()[slot - 1]?.id ?? null;
        battle.chooseSwitch(slot);
        continue;
      }

      // Normal turn — gate on animation cadence so the player can't
      // pre-commit moves before the previous turn's events have played.
      await this.waitForChoicesReady();
      if (this.battleOver) return;
      this.appendLog('What will you do?');
      const action = await this.promptAction();
      if (this.battleOver) return;
      if (action.kind === 'run') return;
      if (action.kind === 'move') battle.chooseMove(action.slot);
      else if (action.kind === 'switch') {
        // v7: same id stamp as the force-switch branch above — keeps
        // activeId tracking consistent across both switch flows.
        this.pendingSwitchToId = runState.getParty()[action.slot - 1]?.id ?? null;
        battle.chooseSwitch(action.slot);
      }
      // action.kind === 'item': nothing to submit — confirmBagUse
      // already fired useItemTurn (the phantom choice) before
      // resolving. Loop back to await the post-item turn's request.
    }
  })();

  // Event loop: drain events as they arrive and play them with delays.
  for await (const event of battle.events()) {
    await this.playEvent(event);
  }
  // playEvent for 'battleEnd' calls exitToWorld; this is the exit hatch.
}

export function appendLog(this: BattleScene, text: string): void {
  // Trimming + last-3-lines policy lives inside the battleLog shell.
  this.battleLogHandle?.append(text);
}

/** Resolve a species' primary type via @pkmn/dex (lazy-loaded, cached
 *  by loadGen3) and tint the corresponding HP bar's track background.
 *  Fire-and-forget — defaults to 'normal' if the lookup misses or the
 *  bar handle is gone (mid-teardown). */
export function applyHpBarType(
  this: BattleScene,
  side: 'p1' | 'p2',
  species: string,
): void {
  void (async () => {
    try {
      const gen = await loadGen3();
      const data = gen.species.get(species);
      const primary = data?.types[0]?.toLowerCase() ?? 'normal';
      const handle = side === 'p1' ? this.playerStatusPanel : this.opponentStatusPanel;
      handle?.setType(primary);
    } catch {
      // Dex load failed (network glitch on first battle, etc.) — leave
      // the tint at the default 'normal'. Non-critical signal.
    }
  })();
}

/**
 * Last-alive critical cinematic gate per design doc rev 6. Fires
 * when:
 *   - Only one player Pokémon is alive (party.length - faintedIds
 *     count = 1), AND
 *   - That Pokémon is below 10 % HP (data-critical = true on the
 *     player HP bar).
 *
 * Both signals are passive (CSS-driven once the dataset attribute
 * + body class are set). Call after every HP / faint / switch
 * change on p1 so the gate latches the right state. Removing
 * either condition cleanly transitions the cinematic off.
 */
export function updateLastAliveCriticalState(this: BattleScene): void {
  const aliveCount = runState.getParty().length - this.faintedIds.size;
  const lastAlive = aliveCount === 1;
  const critical = this.playerHpPercent < 10;
  const trigger = lastAlive && critical;
  this.playerStatusPanel?.setLastAlive(trigger);
  const gameEl = document.getElementById('game');
  if (trigger) {
    gameEl?.classList.add('is-last-alive-critical');
  } else {
    gameEl?.classList.remove('is-last-alive-critical');
  }
}

export function wait(this: BattleScene, ms: number): Promise<void> {
  return new Promise((resolve) => this.time.delayedCall(ms, () => resolve()));
}

/**
 * Refresh the EXP bar fill + level on the player status panel.
 * Reads the live runState entry for the given species (since
 * p1.team is a frozen snapshot at battle start, but EXP is awarded
 * against the live runState during battleEnd). Bar fill computes
 * within-level progress: (exp - expForLevel(level)) /
 * (expForLevel(level+1) - expForLevel(level)).
 *
 * Called from createObjects' initial sync, the |switch|p1 handler,
 * and at battleEnd after each level-up event so multi-level chains
 * visibly tick the bar to 100 %, level placard bumps, bar resets.
 */
export function refreshPlayerExp(this: BattleScene, species: string): void {
  if (!this.playerStatusPanel) return;
  const member = runState.getParty().find((m) => m.species === species);
  if (!member) return;
  this.playerStatusPanel.setLevel(member.level);
  this.playerStatusPanel.setExp(member.exp, member.level);
}
